import _ from 'lodash'
import config from './config'
import i18n from './i18n'
import currency from './currency'
import momentHelper from './moment-helper'
import moveDecimal from 'scoreboard/components/move-decimal'
import moment from 'moment'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import DataType from 'scoreboard/datasets/model/data-type'
import isGeographicOrRollup from '../datasets/is-geographic-or-rollup'
const { PERCENT, CURRENCY, TEXT, YES_NO, DATE, TIME, DATE_TIME, THRESHOLD_ID } = DataType
import isRtl from './is-rtl'

/** Left-to-Right Override Unicode control character */
const LRO = '\u202D'
/** Pop Directional Formatting Unicode control character */
const PDF = '\u202C'

/**
 * Whether to automatically shorten values
 * @type {boolean}
 */
let autoShorten = true

const SCORE = 'score'

/**
 * Formats a numeric value using the following options:
 *
 * @typedef FormatValueOptions
 * @param {Object} options
 * @param {Object} options.value the value to format
 * @param {String} [options.dataType] the dataType, e.g. 'percent', 'number',
 * 'yes-no', 'date', 'text'
 * @param {String} [options.currencyCode] the currency code, e.g. USD
 * @param {Number|String} [options.precision]  a specific precision that should
 * override the
 * globally configured precision. Passing in 'none' means no rounding should be
 * performed
 * @param {String} [options.timeZone] if the field is a datetime field,
 * format it with this timezone offset
 * @param {Boolean} [options.shorten=true] whether we should abbreviate large
 * values like 12345 to 12.3K and (#12783 #18761) disregard decimals for numbers
 * between 100 and 100K
 * @param {Boolean} [options.group=true] pass false to omit grouping symbols
 * (e.g. 1000 instead of 1,000)
 * @param {String} [options.dateFormat=momentHelper.dateFormat] an optional
 * format to use for dates
 * @param {Boolean} [options.forcePrecision=false] pass true
 * to pad result to [options.precision] number of zeros
 * @param {Boolean} [options.moveDecimalForPercent=true] whether values with
 * a dataType of 'percent' should have the decimal shifted for display.
 * @param {Boolean} [options.omitCurrencySymbol=false] do not include symbol
 * when formatting as currency
 * @param {KeyDisplay} [options.keyDisplay] the KeyDisplay object used to
 * format geographic and rollup data
 */
function formatValue(options) {
	if (!options || options.value === '' || options.value == null) {
		return ''
	}

	options = _.extend({
		// #32744 in some cases (preview of import transformations), we don't
		// want to shift the decimal when displaying percents
		moveDecimalForPercent: true,
		omitCurrencySymbol: false
	}, options)

	if (options.dataType === THRESHOLD_ID) {
		return formatThreshold(options.value)
	}

	if (options.dataType === TEXT) {
		return options.value
	}

	if (options.dataType === DATE) {
		let valDateFormat =
			options.dateFormat || momentHelper.dateFormat
		return moment(options.value).format(valDateFormat)
	}

	if (isGeographicOrRollup(options.dataType)) {
		return options.keyDisplay.labelAndKeys(options.value)
	}

	if (options.dataType === TIME) {
		return momentHelper.formatTime(options.value, options.timeFormat)
	}

	if (options.dataType === DATE_TIME) {
		return momentHelper.formatDateTime(options.value, options.timeZone,
			options.timeFormat)
	}

	if (options.dataType === YES_NO) {
		var value = getYesNoValue(options.value)
		if (value === null) {
			return ''
		}
		return i18n.get('common.' + (value ? 'yes' : 'no'))
	}

	// these NaN checks needs to go after we handle yes/no

	if (_.isNaN(options.value)) {
		// #32160 if the value is literally NaN (as opposed to "a String that
		// is not a number"), return an empty String, just like we do for
		// null, undefined, etc. The lodash _.isNaN function uses a stricter test
		return ''
	}

	if (isNaN(options.value)) {
		// #22279 if the value is not a number (but not NaN), just return the
		// original value unchanged. The native isNaN has a broader definition
		// of isNaN, which includes strings that don't parse to a number
		return options.value
	}

	options.value = Number(options.value)

	var raw = options.value

	if (options.moveDecimalForPercent) {
		if (options.dataType === PERCENT ||
			(options.dataType === SCORE && config.showScoresAsPercent)) {
			raw = moveDecimal(options.value, 2)
		}
	}
	var absRaw = Math.abs(raw)

	// whether we should be shortening numbers
	var shorten = autoShorten && _.isNil(options.shorten) || options.shorten
	// if the config option for shortening numbers is set to false, that
	// overrides everything else
	if (!config.shorteningNumbers) {
		shorten = false
	}

	// #12783 values between 100 and 10000 should be rounded to 0 decimal places
	// before running through the code below
	if (shorten && absRaw >= 100 && absRaw < 10000 && options.precision !== 'none') {
		absRaw = round(absRaw, 0)
	}

	// figure out if it's a big number that will use a suffix like 123M
	var shortened, key
	var divisor = 1
	if (shorten) {
		if (config.indianNumbering) {
			// 10 million = one crore
			// 100 thousand = one lahk
			if (absRaw >= 10000000) { // 10 million = 1 crore
				key = 'common.number.cr'
				divisor = 10000000
			} else if (absRaw >= 100000) { // 100 thousand = 1 lahk
				key = 'common.number.L'
				divisor = 100000
				// #12783 only shorten values over 10,000
			} else if (absRaw >= 10000) {
				key = 'common.number.K'
				divisor = 1000
			}
		} else {
			if (absRaw >= 1000000000000) {
				key = 'common.number.T'
				divisor = 1000000000000
			} else if (absRaw >= 1000000000) {
				key = 'common.number.B'
				divisor = 1000000000
			} else if (absRaw >= 1000000) {
				key = 'common.number.M'
				divisor = 1000000
				// #12783 only shorten values over 10,000
			} else if (absRaw >= 10000) {
				key = 'common.number.K'
				divisor = 1000
			}
		}
	}

	var quotient = absRaw / divisor

	// figure out the precision
	var precision = getPrecision(divisor, quotient, options, shorten)

	// fixes #13996 Possible to get abbreviations like 1000K
	// #27527 adjustments for Indian numbering system
	if (config.indianNumbering) {
		if (round(quotient, precision) >= 100) {
			if (divisor == 1000) {
				// rounding up from thousands to lahks
				if (key == 'common.number.K') {
					key = 'common.number.L'
				}
			} else if (divisor == 100000) {
				// rounding up from from lahks to crores
				if (key == 'common.number.L') {
					key = 'common.number.cr'
				}
			}
			if (divisor == 1000 || divisor == 100000) {
				divisor *= 100
				quotient /= 100
				precision = getPrecision(divisor, quotient, options, shorten)
			}
		}
	} else {
		if (divisor != 1 && round(quotient, precision) >= 1000) {
			divisor *= 1000
			quotient /= 1000
			precision = getPrecision(divisor, quotient, options, shorten)
			if (key == 'common.number.B') {
				key = 'common.number.T'
			}
			if (key == 'common.number.M') {
				key = 'common.number.B'
			}
			if (key == 'common.number.K') {
				key = 'common.number.M'
			}
		}
	}
	var roundingPrecision = options.precision === 'none' ? 'none' : precision

	var rounded = round(quotient, roundingPrecision)
	// issue 5238 special handling for currencies so that
	// we display $0, $7 and $7.50 properly, but never $7.5
	if (options.dataType === CURRENCY && divisor === 1 &&
			parseInt(absRaw) != absRaw) {
		// Always force 'en-US' because localizeSeparators depends on '.' and ','
		// We need to apply the separators ourself because browsers don't to this
		// perfectly or consistently using toLocaleString with the browser's locale.
		// The server reads the Accept-Language header and sends the separators
		// back in the config; providing consistency everywhere
		// (#9888, #36256)
		// #47056 if using the Indian numbering system, don't use 'en-US' to
		// localize separators because that will incorrectly provide English
		// locale grouping for currency datatypes
		let locale = config.indianNumbering ? 'en-IN' : 'en-US'
		shortened = localizeSeparators(absRaw.toLocaleString(locale, {
			minimumFractionDigits: precision,
			maximumFractionDigits: precision
		}))
	} else if (options.group !== false) {
		// #12783 only shorten values over 10,000
		shortened = group(rounded, shorten && absRaw >= 10000)
	}

	// apply suffix for numbers like 123M
	if (key) {
		shortened = i18n.get(key, shortened)
	}

	var formatted = shortened || rounded
	var padDecimalPlacesToPrecision = options.forcePrecision &&
		isNumericDataType(options.dataType) &&
		precision !== 'none'
	if (padDecimalPlacesToPrecision) {
		var [leftPlaces, rightPlaces] = formatted.split(config.decimalSeparator)
		var paddedRightPlaces = (rightPlaces || '').padEnd(precision, '0')
		formatted = [leftPlaces, paddedRightPlaces].filter(it => !!it).join(config.decimalSeparator)
	}
	if (options.dataType === PERCENT ||
		(options.dataType === SCORE && config.showScoresAsPercent)) {
		formatted = appendPercentSign(formatted)
	}
	if (options.dataType === CURRENCY && !options.omitCurrencySymbol) {
		formatted = appendCurrencySymbol(formatted, options)
	}
	if (raw < 0 && rounded > 0) {
		// #62217 wrap negative numbers with Unicode LRO (\u202D) and PDF (\u202C)
		// for RTL locales so the minus sign will still be displayed on the left
		const formattedNegative = '-' + formatted
		formatted = isRtl() ? `${LRO}${formattedNegative}${PDF}` : formattedNegative
	}
	return formatted
}

function formatThreshold(value) {
	if (_.isNil(value)) {
		return ''
	} else {
		if (_.isString(value)) value = _.parseInt(value)
		let threshold = config.threshold(value)
		return _.get(threshold, 'label')
	}
}

/**
 * Add grouping and decimal characters to a value.
 *
 * FIXME: This won't work in all languages. In East Asian cultures, particularly
 * China, Japan, and Korea, large numbers are read in groups of myriads
 * (10,000s) and the delimiter accordingly separates every four digits. The
 * Indian numbering system... groups the rightmost three digits in a similar
 * manner to European languages but then groups every two digits thereafter: 1.5
 * million would accordingly be written 15,00,000 and read as "15 lakh".
 *
 * See http://en.wikipedia.org/wiki/Decimal_mark#Digit_grouping
 */
function group(value, shorten) {
	var absoluteValue = _.isNumber(value) ? Math.abs(value) : value
	var absoluteValueStr = '' + absoluteValue
	var point = absoluteValueStr.indexOf('.')
	var s = localizeSeparators(absoluteValueStr)
	var sign = value < 0 ? '-' : ''
	if (shorten) {
		// shortcut for normal case when there is no thousands separator since we
		// are using K, M, B suffixes
		if (s.indexOf('.') < 2 || !config.shorteningNumbers) {
			return sign + s
		}
	}
	if (point == -1) point = s.length
	var grouped = ''
	if (config.indianNumbering) {
		for (var i=0; i<point; i++) {
			grouped += s[i]
			var dist = point - i - 1 // distance from the decimal
			if (dist === 3) { // grouping of 3 to the left of the decimal
				grouped += config.groupingSeparator
			}
			if (dist > 3) {
				// groupings of 2 after the first grouping of 3
				if (((dist + 3) % 2) == 0) {
					grouped += config.groupingSeparator
				}
			}
		}
	} else {
		for (var i=0; i<point; i++) {
			grouped += s[i]
			var dist = point - i - 1 // distance from the decimal
			if (dist != 0 && dist % 3 == 0) {
				grouped += config.groupingSeparator
			}
		}
	}
	var end = s.substr(point+1)
	var answer = sign + grouped
	if (end) {
		answer += config.decimalSeparator + end
	}
	return answer
}

function round(number, precision) {
	if (precision === 'none') {
		return number
	}
	var m = Math.pow(10, precision)
	return Math.round(number * m) / m
}

/**
 * Swaps out English decimal and grouping separators for the appropriate ones
 * for the locale, as passed from the server to the client in
 * config.decimalSeparator and config.groupingSeparator
 */
function localizeSeparators(str) {
	var fixed = ''
	for (var i=0; i<str.length; i++) {
		var char = str.charAt(i)
		if (char === '.') {
			char = config.decimalSeparator
		} else if (char === ',') {
			char = config.groupingSeparator
		}
		fixed += char
	}
	return fixed
}

function appendCurrencySymbol(value, options) {
	var formatted = value
	var code = options.currencyCode || config.defaultCurrencyCode
	var curr = currency.forCode(code)
	var sym = curr && curr.symbol

	code = currency.useCode(code) ? code : null


	if (sym) {
		if (code) {
			if (config.currencyOnLeft) {
				formatted = sym + formatted + ' ' + code
			} else {
				formatted = formatted + ' ' + sym + ' ' + code
			}
		} else {
			if (config.currencyOnLeft) {
				formatted = sym + formatted
			} else {
				formatted = formatted + ' ' + sym
			}
		}
	}
	return formatted
}

function appendPercentSign(value) {
	var formatted = value
	if (config.percentOnLeft) {
		formatted = '%' + formatted
	} else {
		formatted += '%'
	}
	return formatted
}

/**
 * @param shorten the fully resolved value for whether we should be shortening
 * numbers based on options.shorten, the configuration, and the actual value
 * being worked with
 */
function getPrecision(divisor, quotient, options, shorten) {
	var precision

	// #12783 values between 100 and 10000 should not show decimals
	if (shorten && quotient >= 100 && quotient < 10000) {
		return 0
	}

	if (divisor > 1) {
		var decimalIndex = ('' + quotient).indexOf('.')
		if (decimalIndex == -1) {
			precision = 3
		} else {
			precision = 3 - decimalIndex
		}
	} else {
		precision = getPrecisionOrDefault(options.precision, options.dataType)
	}
	return precision
}

/**
 * If the passed in precision is a number, returns that, else uses the dataType to determine
 * the appropriate default from config.js to return
 * @param precision {Number|undefined}
 * @param dataType {String}
 */
function getPrecisionOrDefault(precision, dataType) {
	if (_.isNumber(precision)) return precision

	return dataType === CURRENCY ? config.defaultCurrencyPrecision : config.defaultPrecision
}

/**
 * #37077 Converts a string value into a true or false boolean based on a set
 * of key words
 *
 * @param {String} value the string value to convert
 * @return the boolean value; if the value doesn't match any of the key terms,
 * 		   returns the passed in value
 */
function getYesNoValue(value) {
	if ([ '1', '1.0',
		i18n.get('common.abbreviatedYes').toLowerCase(),
		i18n.get('common.yes').toLowerCase(),
		i18n.get('common.true').toLowerCase(),
		i18n.get('common.abbreviatedTrue').toLowerCase(),
		i18n.get('scorecards.node.yes').toLowerCase()
	].includes(String(value).toLowerCase())) {
		return true
	}
	if ([ '0', '0.0',
		i18n.get('common.abbreviatedNo').toLowerCase(),
		i18n.get('common.no').toLowerCase(),
		i18n.get('common.false').toLowerCase(),
		i18n.get('common.abbreviatedFalse').toLowerCase(),
		i18n.get('scorecards.node.no').toLowerCase()
	].includes(String(value).toLowerCase())) {
		return false
	}
	if (typeof value ==='boolean') {
		return value
	}

	return null
}

// Returns the shortened and unshortened formatted versions of any given values
function shortenedAndUnshortened(data) {
	return {
		shortened: formatValue(data),
		unshortened: formatValue(_.extend({ shorten: false}, data))
	}
}

/**
 * Changes the global behavior to unshorten all formatted values,
 * ignoring any options to shorten values.
 *
 * Intended for use by exports, where only one view is being rendered per window.
 */
function unshortenByDefault() { autoShorten = false }

_.extend(formatValue, {
	group,
	round,
	SCORE,
	localizeSeparators,
	appendCurrencySymbol,
	appendPercentSign,
	shortenedAndUnshortened,
	unshortenByDefault,
	getPrecisionOrDefault,
	formatThreshold,
	LRO,
	PDF
})
export default formatValue
export { group }
export { round }
export { SCORE }
export { localizeSeparators }
export { appendCurrencySymbol }
export { appendPercentSign }
export { shortenedAndUnshortened }
export { unshortenByDefault }
export { getPrecisionOrDefault }
export { formatThreshold }
export { LRO }
export { PDF }

formatValue.__filename__ = 'components/format-value.js'
